<template>
  <!-- A plan ladder: the page's rows of the plan comparison, read from data/pricing-features.json
       (never hard-coded; chapter.labels may shorten a row's name), drawn as bars that start at the first plan that includes the row and run
       to Enterprise. A row whose value changes by plan (Basic, Full, 3, 10…) splits into labelled
       segments. When the ladder scrolls into view the bars grow from their first plan, row by
       row; the resting styles are the final frame (no JS, crawlers, reduce-motion). -->
  <section class="spl">
    <div class="spl-shell">
      <header class="spl-heading">
        <div>
          <h2>{{ chapter.title }}</h2>
        </div>
        <div>
          <p class="spl-intro">{{ chapter.intro }}</p>
          <NuxtLink :to="localePath('/pricing')" class="spl-link">
            {{ chapter.linkLabel }} <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </div>
      </header>

      <div ref="rootEl" class="spl-card" :class="{ 'is-run': running || armed, 'is-armed': armed }">
        <div class="spl-plans" aria-hidden="true">
          <span />
          <span class="spl-plan-cols">
            <span v-for="plan in plans" :key="plan.slug" class="spl-plan">
              <strong translate="no">{{ plan.name }}</strong>
              <small>${{ plan.monthly }}/mo</small>
            </span>
          </span>
        </div>
        <ol class="spl-rows">
          <li v-for="(row, r) in ladder" :key="row.key" class="spl-row" :style="{ '--d': `${0.1 + r * 0.12}s` }">
            <span class="spl-label">
              <strong>{{ row.label }}</strong>
              <small>{{ row.from }}</small>
            </span>
            <span class="spl-track">
              <i
                v-for="segment in row.segments"
                :key="segment.start"
                class="spl-seg"
                :class="{ 'spl-seg--top': segment.top }"
                :style="{ gridColumn: `${segment.start + 1} / ${segment.end + 2}` }"
              >
                <b v-if="segment.text">{{ segment.text }}</b>
              </i>
            </span>
          </li>
        </ol>
        <p v-if="chapter.note" class="spl-foot">{{ chapter.note }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import pricing from "~/data/pricing-features.json";

const props = defineProps({
  // { eyebrow, title, intro, linkLabel, rows: [row keys], everyPlan, andUp ("{plan} and up"), note }
  chapter: { type: Object, required: true },
});

const { localePath } = useWorkspaceLocale();
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.35 });

const plans = pricing.plans;
const allRows = pricing.categories.flatMap((category) => category.rows);
const valueText = (cell) => (cell.startsWith("v:") ? pricing.values[cell.slice(2)] ?? cell : cell);

const ladder = computed(() =>
  props.chapter.rows
    .map((key) => allRows.find((row) => row.key === key))
    .filter(Boolean)
    .map((row) => {
      const first = row.cells.findIndex((cell) => cell !== "no");
      // Runs of the same value become one segment; "yes" needs no text in the bar.
      const segments = [];
      row.cells.forEach((cell, i) => {
        if (cell === "no") return;
        const open = segments[segments.length - 1];
        if (open && open.cell === cell && open.end === i - 1) open.end = i;
        else segments.push({ cell, start: i, end: i, text: cell === "yes" ? "" : valueText(cell) });
      });
      if (segments.length) segments[segments.length - 1].top = segments.length > 1;
      const from = first === 0 ? props.chapter.everyPlan : props.chapter.andUp.replace("{plan}", plans[first]?.name ?? "");
      return { key: row.key, label: props.chapter.labels?.[row.key] ?? row.label, from, segments };
    }),
);
</script>

<style scoped>
.spl { background: #f8fafc; padding: 6.5rem 0; }
.dark .spl { background: #0b1220; }
.spl-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.spl-heading { display: grid; gap: 1.5rem; align-items: end; }
@media (min-width: 1024px) { .spl-heading { grid-template-columns: 1fr 1fr; gap: 2.5rem; } }
.spl-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .spl-heading h2 { color: #f8fafc; }
.spl-intro { color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .spl-intro { color: #cbd5e1; }
.spl-link { display: inline-flex; gap: 0.4rem; margin-top: 1rem; color: #0e7490; font-weight: 800; }
.dark .spl-link { color: #67e8f9; }
.spl-link:hover { text-decoration: underline; }

.spl-card { margin-top: 3rem; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; padding: 1.25rem 1.5rem 1.4rem; }
.dark .spl-card { border-color: #1e293b; background: #0f172a; }
.spl-plans, .spl-row { display: grid; grid-template-columns: minmax(12rem, 1.1fr) minmax(0, 3fr); align-items: center; gap: 1.5rem; }
.spl-plans { padding-bottom: 0.85rem; border-bottom: 1px solid #e2e8f0; }
.dark .spl-plans { border-bottom-color: #1e293b; }
.spl-plan-cols { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 4px; }
.spl-plan { display: grid; gap: 0.1rem; padding-left: 0.6rem; border-left: 1px solid #e2e8f0; line-height: 1.2; }
.dark .spl-plan { border-left-color: #1e293b; }
.spl-plan strong { color: #0f172a; font-size: 0.92rem; font-weight: 800; }
.dark .spl-plan strong { color: #f1f5f9; }
.spl-plan small { color: #64748b; font-size: 0.76rem; }
.dark .spl-plan small { color: #94a3b8; }

.spl-rows { margin: 0; padding: 0; list-style: none; }
.spl-row { min-height: 3.4rem; padding: 0.55rem 0; }
.spl-row + .spl-row { border-top: 1px solid #f1f5f9; }
.dark .spl-row + .spl-row { border-top-color: #1e293b; }
.spl-label { display: grid; line-height: 1.3; }
.spl-label strong { color: #0f172a; font-size: 0.95rem; font-weight: 700; }
.dark .spl-label strong { color: #f1f5f9; }
.spl-label small { color: #0e7490; font-size: 0.8rem; font-weight: 700; }
.dark .spl-label small { color: #67e8f9; }
.spl-track { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 4px; height: 28px; }
.spl-seg {
  display: flex;
  font-style: normal;
  align-items: center;
  min-width: 0;
  border-radius: 4px;
  background: linear-gradient(90deg, #a5f3fc, #67e8f9);
  padding: 0 0.6rem;
  transform-origin: left center;
}
.spl-seg--top { background: linear-gradient(90deg, #0e7490, #155e75); }
.dark .spl-seg { background: linear-gradient(90deg, #155e75, #0e7490); }
.dark .spl-seg--top { background: linear-gradient(90deg, #1ba7ce, #67e8f9); }
.spl-seg b { overflow: hidden; color: #083344; font-size: 0.78rem; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.spl-seg--top b { color: #fff; }
.dark .spl-seg b { color: #ecfeff; }
.dark .spl-seg--top b { color: #083344; }
.spl-foot { margin-top: 1rem; color: #64748b; font-size: 0.82rem; line-height: 1.55; }
.dark .spl-foot { color: #94a3b8; }

.is-run .spl-seg { animation: spl-grow 0.9s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .spl-label small { animation: spl-fade 0.5s ease-out calc(var(--d) + 0.45s) both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes spl-grow { from { transform: scaleX(0); } to { transform: none; } }
@keyframes spl-fade { from { opacity: 0; } to { opacity: 1; } }

/* Tablets and phones: the label sits above its bar, and the plan header keeps only the names. */
@media (max-width: 860px) {
  .spl { padding: 4.5rem 0; }
  .spl-card { padding: 1rem; }
  .spl-plans { grid-template-columns: 1fr; }
  .spl-plans > span:first-child { display: none; }
  .spl-plan { padding-left: 0.35rem; }
  .spl-plan strong { overflow: hidden; font-size: 0.72rem; text-overflow: ellipsis; white-space: nowrap; }
  .spl-plan small { font-size: 0.68rem; }
  .spl-row { grid-template-columns: 1fr; gap: 0.45rem; }
  .spl-track { height: 24px; }
  .spl-seg { padding: 0 0.4rem; }
}
@media (max-width: 520px) {
  /* Plan names don't fit five across; each row already says where it starts. */
  .spl-plans { display: none; }
  .spl-shell { width: min(100% - 2rem, 84rem); }
  /* A one-plan segment is about 47px wide: short values like "Email only" wrap instead of truncating. */
  .spl-seg { padding: 0 0.35rem; }
  .spl-seg b { font-size: 0.66rem; line-height: 1.05; white-space: normal; }
}
@media (prefers-reduced-motion: reduce) { .spl-card * { animation: none !important; } }
</style>
